import { AxiosError, AxiosResponse, isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { Initiative } from '../initiative-client/initiative-client.type';
import type { ProcessAnalysisInitiativeChartData } from '../portfolio-client/portfolio-client.type';
import type { Epic } from '../tasks-client/task-client.type';
import type {
  CreateProjectPayload,
  Project,
  ProjectBoardSprints,
  ProjectsResponse,
  Subproject,
} from './projects-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/projects/';

/**
 * Creates a new project
 *
 * @param project The project data to create
 * @returns The created project
 */
function createProject(project: CreateProjectPayload): Promise<ProjectsResponse> {
  return axiosInstance.post(baseURL, project);
}

/**
 * Fetches a list of projects for the current user.
 *
 * @param orgId incase the projects need to be constrained by orgs
 * @returns ProjectsResponse[]
 */
const fetchProjects = async (orgId?: string | null): Promise<ProjectsResponse[]> => {
  try {
    let apiURL = baseURL;
    if (orgId) {
      apiURL = baseURL + `?organization_id=${orgId}`;
    }
    const response: AxiosResponse = await axiosInstance.get(apiURL);
    return response.data.projects;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching projects.'));
    }
  }
};

/**
 * Fetches a given project for the current user, given a project id (gid).
 *
 * @param gid - project id
 * @returns Project
 */
const fetchProject = (gid: string): Promise<Project> => {
  const apiURL = baseURL + `${gid}/`;
  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
};

/**
 * Updates a project
 *
 * @param project
 * @returns Project
 */
const updateProject = (project: any) => {
  const apiURL = baseURL + `${project.id}/`;
  return axiosInstance
    .put(apiURL, project)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
};

/**
 * Method to fetch the project board sprints
 *
 * @param projectId project Id
 * @returns an object of list of sprints with respect to their boards
 */
function fetchProjectBoardSprints(projectId: string): Promise<ProjectBoardSprints> {
  const url = baseURL + `${projectId}/board-sprints/`;

  return axiosInstance
    .get(url.toString())
    .then(({ data }) => Promise.resolve(data))
    .catch((error) => Promise.reject(error.response || 'Error fetching project board sprints'));
}

/**
 * Method to fetch the initiatives for a given project
 *
 * @param projectId project id to fetch initiatives for
 * @returns a list of initiatives
 */
const getInitiatives = async (projectId: string, signal?: AbortSignal): Promise<Initiative[]> => {
  try {
    const url = baseURL + `${projectId}/initiatives/`;
    const response: AxiosResponse = await axiosInstance.get(url, { signal });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching initiative completions.'));
    }
  }
};

/**
 * Method to fetch the initiative completions for a given portfolio
 *
 * @param projectId project id
 * @param startDate start date to filter
 * @param endDate end date to filter
 * @returns a object containing initiative completions
 */
const getInitiativesCompletion = async (
  projectId: string,
  startDate: string | null,
  endDate: string | null,
  signal?: AbortSignal,
): Promise<ProcessAnalysisInitiativeChartData> => {
  try {
    let url = baseURL + `${projectId}/initiatives/completion/`;
    if (startDate) {
      url += `?start_date=${startDate}`;
    }
    if (endDate) {
      url += `&end_date=${endDate}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url, { signal });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching initiative completions.'));
    }
  }
};

/**
 * Method to fetch the boards for a given team
 *
 * @param teamId team id
 * @returns a list of boards
 */
const getBoardsByTeam = async (teamId: string): Promise<Subproject[]> => {
  const url = baseURL + `${teamId}/subprojects/`;
  const response: AxiosResponse = await axiosInstance.get(url);
  return response.data;
};

/**
 * Fetches epics for a given project id
 * @param projectId project id
 * @returns a list of epics
 */
const getEpicsByProjectId = async (projectId: string): Promise<Epic[]> => {
  const url = baseURL + `${projectId}/epics/`;
  try {
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching epics.'));
    }
  }
};

/**
 * Fetches epics for a given project id and subproject id
 *
 * @param projectId project id
 * @param subprojectId subproject id
 * @returns a list of epics
 */
const getEpicsByProjectIdAndSubprojectId = async (projectId: string, subprojectId: string): Promise<Epic[]> => {
  const url = baseURL + `${projectId}/subprojects/${subprojectId}/epics/`;
  try {
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching epics.'));
    }
  }
};

export {
  createProject,
  fetchProject,
  fetchProjectBoardSprints,
  fetchProjects,
  getBoardsByTeam,
  getEpicsByProjectId,
  getEpicsByProjectIdAndSubprojectId,
  getInitiatives,
  getInitiativesCompletion,
  updateProject,
};
